import {
  applySnapshot,
  cast,
  castToSnapshot,
  flow,
  getSnapshot,
  IMSTArray,
  Instance,
  ISimpleType,
  types,
} from 'mobx-state-tree';
import { toJS } from 'mobx';
import cloneDeep from 'lodash/cloneDeep';
import { Requests } from '../../http-requests';
import { RequestStateModel } from './general-models';
import { parseMaskedPhoneToCodeNumber, prepareParams } from '../../utils';
import {
  filterGroupsByHouseBindingsCallback,
  filterRosclastersProviders,
  getCurrentDictionaryHouseBindingTypeMsg,
} from 'views/authorized/pages/requests/tabs/directories/tabs/phones-directory/utils';
import { IHouseGroups } from 'types';

const StatusesModel = types.model({
  saveProvider: types.optional(RequestStateModel, {}),
  getDirectories: types.optional(RequestStateModel, {}),
});

const PhoneModel = types.model({
  _id: types.maybe(types.string),
  type: types.maybe(types.string),
  code: types.optional(types.string, ''),
  number: types.optional(types.string, ''),
  add: types.maybeNull(types.string),
  phone_name: types.maybeNull(types.string),
});

const EmailModel = types.model({
  _id: types.maybe(types.string),
  email_address: types.optional(types.string, ''),
  email_name: types.maybeNull(types.string),
});

const DepartmentModel = types.model({
  _id: types.maybe(types.string),
  name: types.optional(types.string, ''),
  phones: types.optional(types.array(PhoneModel), []),
  emails: types.optional(types.array(EmailModel), []),
});

const GroupHouseBindingModel = types.model({
  fias: types.optional(types.array(types.string), []),
  house_groups: types.optional(types.array(types.string), []),
  houses: types.optional(types.array(types.string), []),
});

const GroupModel = types.model({
  _id: types.maybe(types.string),
  house_binding: types.optional(GroupHouseBindingModel, {}),
  group_name: types.optional(types.string, ''),
  departments: types.optional(types.array(DepartmentModel), []),
  oldIndexInList: types.maybe(types.number),
});

const ProviderModel = types.model({
  _id: types.maybe(types.string),
  organization: types.optional(types.string, ''),
  description: types.optional(types.string, ''),
  website: types.maybeNull(types.string),
  priority: types.maybe(types.number),
  provider: types.maybe(types.string),
  groups: types.optional(types.array(GroupModel), []),
  oldIndexInList: types.maybe(types.number),
});

const CurrentViewModel = types.model({
  activeView: types.optional(
    types.union(types.literal('create'), types.literal('detail')),
    'create'
  ),
});

const AllAddressesModel = types.model({
  active: types.optional(types.boolean, false),
  allHousesIdsFromFiasTree: types.optional(types.array(types.string), []),
});

const InvalidBeingAddedContactsModel = types.model({
  phone: types.optional(types.boolean, false),
  email: types.optional(types.boolean, false),
});

const ProviderTabModel = types.model({
  _id: types.maybe(types.string),
  name: types.optional(types.string, ''),
  priority: types.maybe(types.number),
  oldIndexInList: types.maybe(types.number),
  isRosclastersDirectoryProvider: types.optional(types.boolean, false),
});

export interface IProviderModel extends Instance<typeof ProviderModel> {}

export interface IGroupModel extends Instance<typeof GroupModel> {}

export interface IDepartmentModel extends Instance<typeof DepartmentModel> {}

export interface IPhoneModel extends Instance<typeof PhoneModel> {}

export interface IEmailModel extends Instance<typeof EmailModel> {}

export interface IProviderTab extends Instance<typeof ProviderTabModel> {}

export interface IAllAddressesModel extends Instance<
  typeof AllAddressesModel
> {}

export const PhoneDirectoriesStore = types
  .model({
    statuses: types.optional(StatusesModel, {}),
    providerList: types.optional(types.array(ProviderModel), []),
    providerListWithPriorityChanged: types.optional(
      types.array(ProviderModel),
      []
    ),
    currentGroup: types.optional(GroupModel, {}),
    currentGroups: types.optional(types.array(GroupModel), []),
    currentProvider: types.optional(ProviderModel, {}),
    editingGroupIds: types.optional(
      types.array(
        types.model({
          isNew: types.boolean,
          id: types.string,
        })
      ),
      []
    ),
    currentView: types.optional(CurrentViewModel, {}),
    allAddresses: types.optional(AllAddressesModel, {}),
    triggeredRebuildFiasSearchHouseBinding: types.optional(
      types.boolean,
      false
    ),
    isRosclastersDirectoryProvider: types.optional(types.boolean, false),
    showValidationErrors: types.optional(types.boolean, false),
    groupEditingFormExist: types.optional(types.boolean, false),
    invalidBeingAddedContacts: types.optional(
      InvalidBeingAddedContactsModel,
      {}
    ),
    selectedGroupIndex: types.maybe(types.number),
  })
  .views((self) => ({
    // для возможности отката изменений priority, сюда передается providerList или providerListWithPriorityChanged
    getProviderListTabs(
      usersProviderId: string,
      searchString?: string,
      withHouseBindingFilter?: {
        // fiasTree: { [propsName: string]: any }[];
        housesGroups: { [propsName: string]: any };
        requestHouseId: string;
      },
      usePriorityList: boolean = false
    ) {
      const providerList = usePriorityList
        ? self.providerListWithPriorityChanged
        : self.providerList;

      let filteredByHouseBindingProviderList: IProviderModel[] | undefined;
      const rosclastersDirectoryProviders = filterRosclastersProviders(
        providerList,
        usersProviderId
      );

      if (withHouseBindingFilter) {
        // Фильтр провайдеров по наличию id дома заявки в привязке по домам (или группе домов)
        filteredByHouseBindingProviderList = providerList.reduce(
          (filtered: IProviderModel[], provider, index) => {
            const includedGroup = provider.groups.some((group) =>
              filterGroupsByHouseBindingsCallback(
                withHouseBindingFilter.housesGroups,
                group,
                withHouseBindingFilter.requestHouseId
              )
            );
            if (includedGroup) {
              filtered.push({ ...provider, oldIndexInList: index });
            }
            return filtered;
          },
          rosclastersDirectoryProviders
        );
      }

      const tabs = (filteredByHouseBindingProviderList || providerList).map(
        (provider, index) => {
          return {
            _id: provider._id,
            isRosclastersDirectoryProvider:
              provider.provider !== usersProviderId,
            name: provider.organization,
            priority: provider.priority,
            oldIndexInList:
              typeof provider.oldIndexInList === 'number'
                ? provider.oldIndexInList
                : index,
            // исходя из задачи, id дома заявки не может присутствовать сразу в нескольких группах одного провайдера
            ...(!!filteredByHouseBindingProviderList && {
              bindingType: getCurrentDictionaryHouseBindingTypeMsg(
                provider.groups[0].house_binding!.houses.length,
                provider.groups[0].house_binding!.house_groups.length
              ),
            }),
          };
        }
      );

      if (searchString) {
        return tabs.filter((tab) => {
          const lowercaseName = tab.name.toLowerCase();
          const lowercaseSearchString = searchString.toLowerCase();

          return (
            lowercaseName.includes(lowercaseSearchString) ||
            lowercaseName
              .split(' ')
              .find((stringPart) => stringPart.includes(lowercaseSearchString))
          );
        });
      }

      return tabs;
    },

    getRosclastersDirectoryProviders(
      usersProviderId: string,
      onlyLength?: boolean
    ) {
      const rosclastersDirectoryProviders = self.providerList.filter(
        (providerObj) => providerObj.provider !== usersProviderId
      );

      return onlyLength
        ? rosclastersDirectoryProviders.length
        : rosclastersDirectoryProviders;
    },

    getFilteredProvidersGroupsByRequestHouseId(
      housesGroups: IHouseGroups[],
      requestHouseId: string
    ) {
      return self.currentProvider.groups.filter(
        (group: IGroupModel) =>
          self.isRosclastersDirectoryProvider ||
          filterGroupsByHouseBindingsCallback(
            housesGroups,
            group,
            requestHouseId
          )
      );
    },

    /*
    Возвращает измененный или нетронутый departments object
    (для удобства отмены сохранения изменений).
    В self.currentGroup можно менять значения.
    */
    getCurrentGroupContactsDepartment(
      groupIndex: number,
      departmentIndex: number
    ) {
      return self.currentGroup._id ===
        self.currentProvider.groups[groupIndex]._id
        ? self.currentGroup.departments[departmentIndex] // копия для изменений
        : self.currentProvider.groups[groupIndex].departments[departmentIndex]; // неизменяемые значения
    },
    getUntouchedGroup() {
      return self.currentProvider.groups.find(
        (group) => group._id === self.currentGroup._id
      );
    },
    /*
      Вернет список id домов, привязанные к группе другим работником, отсутствующие в древе fias текущего работника
      (из-за отсутствия прав на них) 
    */
    getUnavailableToSetGroupHouseBinding(untouchedGroup: IGroupModel) {
      return (
        untouchedGroup.house_binding?.houses.filter(
          (houseId) =>
            !self.allAddresses.allHousesIdsFromFiasTree.includes(houseId)
        ) || []
      );
    },
    /*
    Вернет список id групп домов, привязанные к группе другим работником,
    отсутствующие в houseGroups текущего работника
    (из-за отсутствия прав на них)
    */
    getUnavailableToSetGroupHouseGroupsBinding(
      fiasStorHouseGroups: any[],
      untouchedGroup: IGroupModel
    ) {
      return untouchedGroup.house_binding!.house_groups.length
        ? untouchedGroup.house_binding!.house_groups.filter(
            (houseGroupId) =>
              !fiasStorHouseGroups.some(
                (groupObj) => groupObj.id === houseGroupId
              )
          )
        : [];
    },
  }))
  .actions((self) => ({
    setCurrentProviderByIndex(list: IProviderModel[], indexInList: number) {
      self.currentProvider = {
        ...toJS(list[indexInList]),
        oldIndexInList: indexInList,
      };
    },

    setCurrentProviderById(providerId: string) {
      const provider = self.providerList.find((p) => p._id === providerId);
      if (!provider) return;

      self.currentProvider = cloneDeep(provider);
    },

    setCurrentProviderField(field: string, payload: string | IGroupModel[]) {
      self.currentProvider = { ...self.currentProvider, [field]: payload };
    },

    setCurrentGroupDepartmentContacts(
      groupIndex: number,
      departmentIndex: number,
      field: 'phones' | 'emails',
      payload: any
    ) {
      self.currentGroups[groupIndex].departments[departmentIndex][field] =
        payload;
    },

    setCurrentGroupDepartmentContact(
      departmentIndex: number,
      contactIndex: number,
      field: 'phones' | 'emails',
      contactField:
        | 'phone'
        | 'add'
        | 'phone_name'
        | 'email_address'
        | 'email_name',

      contactValue: string
    ) {
      const department = self.currentGroup.departments[departmentIndex];

      department[field].splice(contactIndex, 1, {
        ...department[field][contactIndex],
        ...(contactField === 'phone' && {
          ...parseMaskedPhoneToCodeNumber(contactValue),
        }),
        ...(contactField === 'add' && {
          [contactField]: contactValue.replace(/[^0-9]/g, ''),
        }),
        ...(['phone_name', 'email_address', 'email_name'].includes(
          contactField
        ) && {
          [contactField]: contactValue,
        }),
      });
    },

    setAllAddresses(
      active?: boolean,
      fiasTreeHouses?: IMSTArray<ISimpleType<string>>
    ) {
      if (typeof active === 'boolean') {
        self.allAddresses.active = active;
      }

      if (fiasTreeHouses) {
        self.allAddresses.allHousesIdsFromFiasTree = fiasTreeHouses;
      }
    },

    setShowValidationErrors(payload: boolean) {
      self.showValidationErrors = payload;
    },

    setGroupEditingFormExist(payload: boolean) {
      self.groupEditingFormExist = payload;
    },

    triggerRebuildFiasSearchHouseBinding(payload: boolean) {
      self.triggeredRebuildFiasSearchHouseBinding = payload;
    },

    deleteDepartmentContact(
      groupIndex: number,
      departmentIndex: number,
      contactIndex: number,
      field: 'phones' | 'emails'
    ) {
      self.currentGroups[groupIndex].departments[departmentIndex][field].splice(
        contactIndex,
        1
      );
    },

    setDepartmentName(
      groupIndex: number,
      departmentIndex: number,
      payload: string
    ) {
      self.currentGroups[groupIndex].departments[departmentIndex].name =
        payload;
    },

    addCurrentGroupEmptyDepartment(groupIndex: number) {
      if (
        !self.currentGroups[groupIndex].departments.some(
          (el: any) => !el.name && !el.emails.length && !el.phones.length
        )
      ) {
        self.currentGroups[groupIndex].departments = castToSnapshot([
          ...self.currentGroups[groupIndex].departments,
          {
            name: '',
            emails: [],
            phones: [],
          },
        ]);
      }
    },

    addEmptyGroup() {
      if (self.currentGroups.length === 0) {
        self.currentGroups.push({
          group_name: '',
          house_binding: { houses: [], house_groups: [] },
          departments: [],
          oldIndexInList: undefined,
          _id: crypto.randomUUID(),
        });
      }
    },

    addCurrentGroup() {
      const newGroup = {
        group_name: '',
        house_binding: { houses: [], house_groups: [] },
        departments: [
          {
            name: '',
            emails: [],
            phones: [],
          },
        ],
        oldIndexInList: undefined,
        _id: crypto.randomUUID(),
      };

      self.currentGroups.unshift(newGroup);
    },

    updateCurrentGroupField(index: number, field: string, payload: string) {
      const group = self.currentGroups[index];
      if (!group) return;

      const snapshot = getSnapshot(group);
      applySnapshot(group, {
        ...snapshot,
        [field]: payload,
      });
    },

    updateGroupDepartmentContact(
      groupIndex: number,
      departmentIndex: number,
      contactIndex: number,
      field: 'phones' | 'emails',
      contactField: string,
      contactValue: string
    ) {
      const group = self.currentGroups[groupIndex];
      const department = group.departments[departmentIndex];

      department[field].splice(contactIndex, 1, {
        ...department[field][contactIndex],
        ...(contactField === 'phone' && {
          ...parseMaskedPhoneToCodeNumber(contactValue),
        }),
        ...(contactField === 'add' && {
          [contactField]: contactValue.replace(/[^0-9]/g, ''),
        }),
        ...(['phone_name', 'email_address', 'email_name'].includes(
          contactField
        ) && {
          [contactField]: contactValue,
        }),
      });
    },

    deleteGroup(groupId: string) {
      const excludedIds = self.editingGroupIds
        .filter((item) => item.isNew)
        .map((item) => item.id);

      const groupIndex = self.currentGroups.findIndex(
        (group) => group._id === groupId
      );

      self.currentProvider.groups.splice(groupIndex - excludedIds.length, 1);
    },

    deleteGroupFromGroups(groupIndex: number) {
      self.currentGroups.splice(groupIndex, 1);
    },

    deleteGroupDepartment(groupIndex: number, departmentIndex: number) {
      self.currentGroups[groupIndex].departments.splice(departmentIndex, 1);
    },

    groupToEdit(groupIndex: number) {
      const currProviderCopy = toJS(self.currentProvider);

      self.currentGroup = {
        ...currProviderCopy.groups.splice(groupIndex, 1)[0],
        oldIndexInList: groupIndex,
      };
    },

    setProviderGroupsToCurrentGroups() {
      const currProviderCopy = toJS(self.currentProvider);
      self.currentGroups = cast(toJS(currProviderCopy.groups));
    },

    setProviderGroupToCurrentGroups(groupId: string) {
      const excludedIds = (self.editingGroupIds || [])
        .filter((item) => item.isNew)
        .map((item) => item.id);

      const groupIndex = self.currentGroups.findIndex(
        (group) => group._id === groupId
      );
      const group =
        self.currentProvider.groups[groupIndex - excludedIds.length];

      self.currentGroups[groupIndex] = cast(toJS(group));
    },

    setGroupToProviderGroup(groupId: string) {
      const excludedIds = (self.editingGroupIds || [])
        .filter((item) => item.isNew)
        .map((item) => item.id);

      const groupIndex = self.currentGroups.findIndex(
        (group) => group._id === groupId
      );
      const group = self.currentGroups[groupIndex];

      self.currentProvider.groups[groupIndex - excludedIds.length] = cast(
        toJS(group!)
      );
    },

    moveGroupToEnd(groupIndex: number) {
      // перемещение группы в конец self.currentGroups
      const snapshot = getSnapshot(self.currentGroups);
      const groupToMove = snapshot[groupIndex];
      if (!groupToMove) return;

      const newGroups = [
        ...snapshot.slice(0, groupIndex),
        ...snapshot.slice(groupIndex + 1),
        groupToMove,
      ];

      applySnapshot(self.currentGroups, newGroups);

      // добавление группы в конец self.currentProvider.groups
      self.currentProvider.groups.push(groupToMove);
    },

    addEditingGroupId(id: string, isNew: boolean) {
      const exists = self.editingGroupIds.some((item) => item.id === id);
      if (!exists) {
        self.editingGroupIds.push({ id, isNew });
      }
    },

    removeEditingGroupId(id: string) {
      self.editingGroupIds.replace(
        self.editingGroupIds.filter((item) => item.id !== id)
      );
    },

    clearEditingGroupIds() {
      self.editingGroupIds.clear();
    },

    shiftEditingGroupIds(deletedId: string) {
      self.editingGroupIds.replace(
        self.editingGroupIds.filter((item) => item.id !== deletedId)
      );
    },

    setCurrentView(tab: 'detail' | 'create') {
      self.currentView = {
        activeView: tab,
      };
    },

    setIsRosclastersDirectoryProvider(payload: boolean) {
      self.isRosclastersDirectoryProvider = payload;
    },

    setInvalidAddContact(contactType: 'email' | 'phone', payload: boolean) {
      self.invalidBeingAddedContacts = {
        ...self.invalidBeingAddedContacts,
        [contactType]: payload,
      };
    },

    clearCurrentProvider() {
      applySnapshot(self.currentProvider, {});
    },

    clearProviderListWithPriorityChanged() {
      applySnapshot(self.providerListWithPriorityChanged, []);
    },

    clearCurrentGroup() {
      applySnapshot(self.currentGroup, {});
    },

    clearCurrentGroups() {
      applySnapshot(self.currentGroups, []);
    },

    reverseCurrentGroups() {
      self.currentGroups.replace([...self.currentGroups].reverse());
    },

    clearPhoneDirectoriesStoreModel() {
      applySnapshot(self.providerList, []);
      applySnapshot(self.statuses, {});
      applySnapshot(self.providerListWithPriorityChanged, []);
      applySnapshot(self.currentGroup, {});
      applySnapshot(self.currentProvider, {});
      applySnapshot(self.currentView, {});
      applySnapshot(self.invalidBeingAddedContacts, {});
      self.showValidationErrors = false;
    },

    setProviderListWithPriority(payload: any) {
      self.providerListWithPriorityChanged = toJS(payload);
    },

    setPriorityInListWithPriority(
      action: 'increase' | 'decrease',
      tabIndex: number
    ) {
      const listCopy = [...toJS(self.providerListWithPriorityChanged)];
      const nextTab = action === 'increase' ? tabIndex - 1 : tabIndex + 1;

      listCopy.splice(nextTab, 1, {
        ...toJS(self.providerListWithPriorityChanged[tabIndex]),
        priority: self.providerListWithPriorityChanged[nextTab].priority,
      });
      listCopy.splice(tabIndex, 1, {
        ...toJS(self.providerListWithPriorityChanged[nextTab]),
        priority: self.providerListWithPriorityChanged[tabIndex].priority,
      });

      self.providerListWithPriorityChanged = cast(toJS(listCopy));
    },

    getRequestPhoneDirectoryProviders: flow(function* () {
      self.statuses.getDirectories.error = false;
      self.statuses.getDirectories.fulfilled = false;
      self.statuses.getDirectories.pending = true;
      try {
        const { data } = yield Requests.GetRequestPhoneDirectoryProviders();
        self.providerList = data;
        self.currentProvider = data[0];
        self.statuses.getDirectories.fulfilled = true;
      } catch (e) {
        self.statuses.getDirectories.error = true;
        console.error(e);
      } finally {
        self.statuses.getDirectories.pending = false;
      }
    }),

    savePhoneDirectoryProvider: flow(function* (payload?: any) {
      if (payload?.group_name) {
        self.currentProvider.groups.splice(
          payload._id
            ? payload.oldIndexInList
            : self.currentProvider.groups.length,
          payload._id ? 1 : 0,
          { ...toJS(payload) }
        );

        applySnapshot(self.currentGroup, {});
      }

      self.statuses.saveProvider.loadingStates();

      const params = prepareParams({ ...toJS(self.currentProvider) }, [
        '',
        undefined,
        null,
      ]);

      if (!params.website) {
        params.website = null;
      }

      try {
        const response = yield Requests.PatchRequestPhoneDirectoryProvider(
          self.currentProvider._id!,
          params
        );

        const formatResponse = {
          ...response,
          _id: response.id,
          groups: response.groups.map((el: any) => ({
            ...el,
            house_binding: el.house_binding || {
              house_groups: [],
              houses: [],
            },
          })),
        };

        self.currentProvider = {
          ...formatResponse,
          oldIndexInList: self.currentProvider.oldIndexInList,
        };
        const providersOldIndex = self.providerList.findIndex(
          (provider) => provider._id === self.currentProvider._id
        );
        if (providersOldIndex >= 0) {
          self.providerList.splice(providersOldIndex, 1, formatResponse);
        }

        self.statuses.saveProvider.successStates();
      } catch (e) {
        // откат изменений
        const findedInList = self.providerList.find(
          (provider: any) => provider._id === self.currentProvider._id
        );
        if (findedInList) {
          self.currentProvider = toJS(findedInList);
        }
        console.error(e);
        self.statuses.saveProvider.errorStates();
      } finally {
        self.statuses.saveProvider.finallyStates();
      }
    }),

    createPhoneDirectoryProvider: flow(function* (
      groupsPayload?: IGroupModel[],
      userStoreCurrentProviderId?: string
    ) {
      if (groupsPayload?.length) {
        groupsPayload.forEach((group) => {
          const groupClone = {
            ...toJS(group),
          };
          self.currentProvider.groups.push(groupClone);
        });
      }

      self.statuses.saveProvider.loadingStates();

      const params = prepareParams({ ...toJS(self.currentProvider) }, [
        '',
        undefined,
        null,
      ]);

      if (!params.website) {
        params.website = null;
      }

      try {
        const response =
          yield Requests.CreateRequestPhoneDirectoryProvider(params);

        const formatResponse = {
          ...response,
          _id: response.id,
          groups: response.groups.map((el: any) => ({
            ...el,
            house_binding: el.house_binding || {
              house_groups: [],
              houses: [],
            },
          })),
        };

        self.currentProvider = {
          ...formatResponse,
          oldIndexInList: self.currentProvider.oldIndexInList,
        };

        let l = self.providerList.length;
        while (l--) {
          if (self.providerList[l].provider !== userStoreCurrentProviderId) {
            // созданная организация справочника отобразится под организациями справочника Роскластера
            self.providerList.splice(l + 1, 0, formatResponse);
            break;
          }
        }
        if (l === -1) {
          // если организации справочника Роскластера отсутствуют
          self.providerList.unshift(formatResponse);
        }

        self.statuses.saveProvider.successStates();
      } catch (e) {
        // откат изменений
        const findedInList = self.providerList.find(
          (provider: any) => provider._id === self.currentProvider._id
        );
        if (findedInList) {
          self.currentProvider = toJS(findedInList);
        }
        console.error(e);
        self.statuses.saveProvider.errorStates();
      } finally {
        self.statuses.saveProvider.finallyStates();
      }
    }),

    deletePhoneDirectoryProvider: flow(function* (providerId: string) {
      try {
        yield Requests.DeleteRequestPhoneDirectoryProvider(providerId);
      } catch (e) {
        console.error(e);
      }
    }),

    findDifferenceAndSavePriority: flow(function* () {
      const diff = self.providerListWithPriorityChanged.filter(
        (provider, index) => {
          return provider._id !== self.providerList[index]._id;
        }
      );

      if (diff.length) {
        /*
          В params.data будет object, ключами которого будут _id организаций с измененными priority, а значениями
          сами priority
        */
        try {
          yield Requests.PatchProvidersPriority({
            data: Object.fromEntries(
              diff.map((provider) => [provider._id, provider.priority])
            ),
          });
        } catch (e) {
          console.error(e);
        }
      }
    }),

    setSelectedGroupIndex(index: number) {
      self.selectedGroupIndex = index;
    },

    clearSelectedGroupIndex() {
      self.selectedGroupIndex = undefined;
    },
  }));
